/**
 * AutoSaveIndicator — small floating pill that shows save state.
 *
 * Uses the ContentEditingContext to display:
 *   - "Saving..." when writes are in flight
 *   - "Saved" briefly after a successful save
 *   - "X pending" when there are queued changes
 *   - Error message on failure
 *
 * Positioned fixed bottom-right. Only visible inside template editing.
 */

import { useEffect, useState, useRef } from "react";
import { Cloud, CloudOff, Loader2, Check } from "lucide-react";
import { useContentEditingSafe } from "./ContentEditingContext";

export function AutoSaveIndicator() {
  const ctx = useContentEditingSafe();
  if (!ctx) return null;
  return <AutoSaveIndicatorInner />;
}

function AutoSaveIndicatorInner() {
  const { isSaving, pendingCount, saveError } = useContentEditingSafe()!;
  const [showSaved, setShowSaved] = useState(false);
  const wasSaving = useRef(false);

  // After saving completes, show "saved" for 2 seconds
  useEffect(() => {
    if (wasSaving.current && !isSaving && !saveError) {
      setShowSaved(true);
      const t = setTimeout(() => setShowSaved(false), 2500);
      return () => clearTimeout(t);
    }
    wasSaving.current = isSaving;
  }, [isSaving, saveError]);

  // Don't show anything when idle
  if (!isSaving && !showSaved && pendingCount === 0 && !saveError) return null;

  let icon: React.ReactNode;
  let text: string;
  let bgColor: string;

  if (saveError) {
    icon = <CloudOff className="w-3.5 h-3.5" />;
    text = "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E9\u05DE\u05D9\u05E8\u05D4";
    bgColor = "#dc2626";
  } else if (isSaving) {
    icon = <Loader2 className="w-3.5 h-3.5 animate-spin" />;
    text = "\u05E9\u05D5\u05DE\u05E8...";
    bgColor = "#3b82f6";
  } else if (showSaved) {
    icon = <Check className="w-3.5 h-3.5" />;
    text = "\u05E0\u05E9\u05DE\u05E8";
    bgColor = "#16a34a";
  } else if (pendingCount > 0) {
    icon = <Cloud className="w-3.5 h-3.5" />;
    text = `${pendingCount} \u05E9\u05D9\u05E0\u05D5\u05D9\u05D9\u05DD \u05D1\u05D4\u05DE\u05EA\u05E0\u05D4`;
    bgColor = "#f59e0b";
  } else {
    return null;
  }

  return (
    <div
      className="fixed bottom-4 right-4 z-[999] flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] text-white shadow-lg"
      dir="rtl"
      style={{
        backgroundColor: bgColor,
        fontWeight: 600,
        fontFamily: "var(--font-family)",
        transition: "background-color 0.3s, opacity 0.3s",
      }}
    >
      {icon}
      <span>{text}</span>
    </div>
  );
}
